import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronRight, ChevronDown, Search, ArrowUpDown, Star, Check } from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import {
  STATUS_COLORS, STATUS_LABELS, fmtTime, todayLocal, TIMELINE_STAGES,
  PENDING_STATUSES, UPCOMING_STATUSES, COMPLETED_STATUSES, CANCELLED_STATUSES,
  experienceBooking,
} from './mentorData';
import { experienceDisplay, lifecycleState } from '@/lib/experienceStatus';

const toBooking = experienceBooking;

const FILTERS = [
  { id: 'pending', label: 'Pending', test: e => PENDING_STATUSES.includes(e.status) },
  { id: 'upcoming', label: 'Upcoming', test: e => ['upcoming', 'in_progress'].includes(lifecycleState(toBooking(e))) },
  { id: 'awaiting', label: 'Awaiting completion', test: e => lifecycleState(toBooking(e)) === 'awaiting_completion' },
  { id: 'completed', label: 'Completed', test: e => COMPLETED_STATUSES.includes(e.status) },
  { id: 'cancelled', label: 'Cancelled', test: e => CANCELLED_STATUSES.includes(e.status) },
];

const awaitingCompletion = (e) => lifecycleState(toBooking(e)) === 'awaiting_completion';
const statusText = (e) => experienceDisplay(toBooking(e)).label;
const sessionLabel = (e) => `${e.offline ? 'Workplace visit' : 'Online session'} · ${e.studentCount || 1} student${(e.studentCount || 1) === 1 ? '' : 's'}`;

const studentNames = (e) => (e.students || []).map(s => s.child_name).filter(Boolean);
const studentsText = (e) => {
  const names = studentNames(e);
  if (names.length > 1) return `${names[0].split(' ')[0]} +${names.length - 1}`;
  return names[0] || e.childName || `${e.studentCount || 1} student`;
};

const shortDate = (iso) => (iso ? formatDate(String(iso).split('T')[0]) : '');

const StatusBadge = ({ e, testId }) => (
  <Badge className={`border-0 text-[10px] ${experienceDisplay(toBooking(e)).tone}`} data-testid={testId}>
    {statusText(e)}
  </Badge>
);

const Rating = ({ rating, testId }) => (rating?.average ? (
  <span className="flex items-center gap-1 text-sm text-[#4B5563]" data-testid={testId}>
    <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
    {rating.average.toFixed(1)}
    <span className="text-[#9CA3AF]">({rating.count} of {rating.total})</span>
  </span>
) : <span className="text-[#C8CDD6] text-xs" data-testid={testId}>—</span>);

const StageText = ({ timeline, testId }) => (
  <span className="text-sm text-[#4B5563]" data-testid={testId}>
    {timeline?.stage?.label}
    {timeline?.stage?.date ? <span className="text-[#9CA3AF]"> · {shortDate(timeline.stage.date)}</span> : null}
  </span>
);

function ExpandedDetail({ e }) {
  const reachedIndex = e.timeline?.stage?.index ?? 0;
  return (
    <div className="bg-[#FAFBFC] px-4 py-3 space-y-3" data-testid={`expanded-${e.id}`}>
      <div className="space-y-1.5">
        {(e.students || []).map((s, i) => (
          <div key={s.booking_id || i} className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-3 border-l-2 border-[#E5E7EB]" data-testid={`student-${e.id}-${i}`}>
            <span className="text-xs font-semibold text-[#1F2937]">{s.child_name}</span>
            {s.child_grade && <span className="text-[11px] text-[#9CA3AF]">Grade {s.child_grade}</span>}
            <Badge className={`border-0 text-[10px] ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>
              {STATUS_LABELS[s.status] || s.status}
            </Badge>
            {s.feedback_rating ? (
              <span className="flex items-center gap-1 text-[11px] text-[#4B5563]">
                <Star className="h-3 w-3 text-amber-400 fill-amber-400" />{s.feedback_rating}
              </span>
            ) : <span className="text-[11px] text-[#C8CDD6]">Not rated</span>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid={`timeline-${e.id}`}>
        {TIMELINE_STAGES.map((stage, i) => {
          const s = e.timeline?.stages?.[i];
          const done = s?.reached && i <= reachedIndex;
          return (
            <span key={stage.id} className={`flex items-center gap-1.5 text-[11px] ${done ? 'text-[#4B5563]' : 'text-[#C8CDD6]'}`}>
              {done ? <Check className="h-3 w-3 text-emerald-500" /> : <span className="h-1.5 w-1.5 rounded-full bg-[#E5E7EB]" />}
              {stage.label}{done && s.date ? ` · ${shortDate(s.date)}` : ''}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function MentorExperienceTable({ experiences, offline = false, onComplete, emptyLabel }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'date', dir: -1 });
  const [open, setOpen] = useState({});

  const toggleOpen = (id) => setOpen(o => ({ ...o, [id]: !o[id] }));

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const f = FILTERS.find(x => x.id === filter);
    const list = experiences.filter(e => {
      if (f && !f.test(e)) return false;
      if (!q) return true;
      return [e.career, e.childName, e.venue, sessionLabel(e), ...studentNames(e)]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
    const val = (e) => ({
      id: e.id || '',
      session: sessionLabel(e).toLowerCase(),
      students: studentsText(e).toLowerCase(),
      career: (e.career || '').toLowerCase(),
      rating: e.rating?.average ?? -1,
      date: `${e.date || '0000'}|${e.id}`,
      status: statusText(e),
      timeline: e.timeline?.stage?.index ?? -1,
    }[sort.key]);
    return [...list].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
  }, [experiences, query, filter, sort]);

  // Newest first by default; rating, timeline and date all read best highest-first
  const toggleSort = (key) => setSort(s => ({
    key, dir: s.key === key ? -s.dir : (['rating', 'timeline', 'date'].includes(key) ? -1 : 1),
  }));
  const Th = ({ label, sortKey, className = '' }) => (
    <th className={`px-3 py-2 text-left text-xs font-bold text-[#9CA3AF] uppercase tracking-wider ${className}`}>
      {sortKey ? (
        <button onClick={() => toggleSort(sortKey)} className="flex items-center gap-1 hover:text-[#1F2937] transition-colors" data-testid={`mentor-sort-${sortKey}`}>
          {label}<ArrowUpDown className="h-3 w-3" />
        </button>
      ) : label}
    </th>
  );

  const CompleteButton = ({ e }) => (awaitingCompletion(e) && onComplete ? (
    <Button size="sm" onClick={(ev) => { ev.stopPropagation(); onComplete(e); }}
      className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-7 px-3 text-[11px] font-semibold"
      data-testid={`complete-${e.id}`}>
      Complete
    </Button>
  ) : null);

  return (
    <div data-testid={offline ? 'mentor-visit-table' : 'mentor-online-table'}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={e => setQuery(e.target.value)}
            placeholder={offline ? 'Search student, career or workplace' : 'Search student or career'}
            className="pl-8 h-9 rounded-lg border-gray-200 text-xs bg-white" data-testid="mentor-experience-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[{ id: 'all', label: `All (${experiences.length})` },
            ...FILTERS.map(f => ({ id: f.id, label: `${f.label} (${experiences.filter(f.test).length})` }))].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${filter === f.id ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`}
              data-testid={`mentor-filter-${f.id}`}>{f.label}</button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-center py-12 text-[#9CA3AF] text-sm" data-testid="mentor-experience-empty">
          {query || filter !== 'all' ? 'Nothing matches this search or filter.' : emptyLabel}
        </p>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <table className="w-full" data-testid="mentor-experience-table-desktop">
              <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">
                <tr>
                  <Th label="Session" sortKey="session" />
                  <Th label="Date & time" sortKey="date" />
                  <Th label="Students" sortKey="students" />
                  <Th label="Career" sortKey="career" />
                  <Th label="Overall rating" sortKey="rating" />
                  <Th label="Status" sortKey="status" />
                  <Th label="Timeline" sortKey="timeline" />
                  <Th label="" className="text-right" />
                </tr>
              </thead>
              <tbody>
                {rows.map(e => (
                  <React.Fragment key={e.id}>
                    <tr onClick={() => toggleOpen(e.id)}
                      className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFBFC] transition-colors cursor-pointer"
                      data-testid={`experience-${e.id}`}>
                      <td className="px-3 py-2.5">
                        <p className="text-xs font-semibold text-[#1F2937] whitespace-nowrap" data-testid={`session-label-${e.id}`}>{sessionLabel(e)}</p>
                        {offline && e.venue && <p className="text-[10px] text-[#9CA3AF]">{e.venue}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563] whitespace-nowrap">
                        {e.date ? <>{formatDate(e.date)}<span className="text-[#9CA3AF]">{e.time ? ` · ${fmtTime(e.time)}` : ''}</span></> : <span className="text-[#C8CDD6]">Not scheduled</span>}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563]" data-testid={`students-${e.id}`}>{studentsText(e)}</td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563]">{e.career || 'Career Experience'}</td>
                      <td className="px-3 py-2.5"><Rating rating={e.rating} testId={`rating-${e.id}`} /></td>
                      <td className="px-3 py-2.5"><StatusBadge e={e} testId={`mentor-status-${e.id}`} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><StageText timeline={e.timeline} testId={`stage-${e.id}`} /></td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <CompleteButton e={e} />
                          <button onClick={(ev) => { ev.stopPropagation(); navigate(e.link); }}
                            className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] transition-colors"
                            data-testid={`view-details-${e.id}`}>
                            Details
                          </button>
                          {open[e.id]
                            ? <ChevronDown className="h-4 w-4 text-[#9CA3AF]" />
                            : <ChevronRight className="h-4 w-4 text-[#C8CDD6]" />}
                        </div>
                      </td>
                    </tr>
                    {open[e.id] && (
                      <tr className="border-b border-[#F3F4F6] last:border-0">
                        <td colSpan={8} className="p-0"><ExpandedDetail e={e} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-2" data-testid="mentor-experience-cards">
            {rows.map(e => (
              <div key={e.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`experience-card-${e.id}`}>
                <button onClick={() => toggleOpen(e.id)} className="w-full px-4 py-3 text-left">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1F2937] truncate">{sessionLabel(e)}</p>
                      {offline && e.venue && <p className="text-[11px] text-[#9CA3AF] truncate">{e.venue}</p>}
                    </div>
                    <StatusBadge e={e} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-[#4B5563]">
                    <span>{e.date ? `${formatDate(e.date)}${e.time ? ` · ${fmtTime(e.time)}` : ''}` : 'Not scheduled'}</span>
                    <span>{studentsText(e)}</span>
                    <Rating rating={e.rating} />
                  </div>
                  <div className="mt-1"><StageText timeline={e.timeline} /></div>
                </button>
                {open[e.id] && <ExpandedDetail e={e} />}
                <div className="px-4 pb-3 flex items-center gap-2">
                  <CompleteButton e={e} />
                  <button onClick={() => navigate(e.link)} className="text-[11px] font-semibold text-[#6B7280]" data-testid={`view-details-card-${e.id}`}>Details</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
